/**
 * The boxes of a focus of several frames (plan 13): one compact group
 * centred on the surface. The main frame stands at its own size; the others
 * stack in a column to its right, top to bottom. Frames are never enlarged,
 * and the group takes at most `ROUND_GROUP_MAX_W_RATIO` ×
 * `ROUND_GROUP_MAX_H_RATIO` of the surface inside its margins, so the
 * desktop stays visible around it. A main frame that stays (a side frame
 * closed, a window placed) is an anchor: it keeps its box and only the
 * column is laid out again beside it.
 *
 * When a round is too big, a reading frame (a document, a list) keeps its
 * width and gives up height, since it scrolls: the reading frames share the
 * height that is left. Only object, media and tool frames scale as a whole.
 * The column keeps a readable width; when even that cannot fit, each frame
 * gets less height, never less width.
 */
import {
  FOCUS_WINDOW_MARGIN,
  roundBox,
  type FocusWindowBox,
} from "@/lib/focus-window-layout";
import {
  isValidOriginalSize,
  type FocusOriginalSize,
} from "@/lib/focus/focus-container-boxes";

/** The share of the surface, inside its margins, a round may take. */
export const ROUND_GROUP_MAX_W_RATIO = 0.8;
export const ROUND_GROUP_MAX_H_RATIO = 0.86;
/** The column is this wide at most, and at least `ROUND_FRAME_MIN_W`. */
export const ROUND_COLUMN_MAX_W = 380;
/** A frame in a round keeps at least this size while the surface allows. */
const ROUND_FRAME_MIN_W = 260;
const ROUND_FRAME_MIN_H = 160;

interface Part {
  width: number;
  height: number;
  /** Scrolls: gives up height before anything else. */
  reading: boolean;
}

function partOf(size: FocusOriginalSize): Part {
  const valid = isValidOriginalSize(size) ? size : { width: 400, height: 300 };
  return {
    width: Math.max(valid.width, ROUND_FRAME_MIN_W),
    height: Math.max(valid.height, ROUND_FRAME_MIN_H),
    reading: size.container === "reading",
  };
}

/** `part` no wider than `width`: a reading frame narrows, others scale. */
function narrowTo(part: Part, width: number): Part {
  if (part.width <= width) return part;
  if (part.reading) return { ...part, width };
  return { ...part, width, height: (part.height * width) / part.width };
}

/** `part` no taller than `height`: a reading frame shortens, others scale. */
function shortenTo(part: Part, height: number): Part {
  if (part.height <= height) return part;
  if (part.reading) return { ...part, height };
  return { ...part, height, width: (part.width * height) / part.height };
}

/** Heights shared out: the shortest keep theirs, the rest split what is left. */
function shareHeights(heights: number[], available: number): number[] {
  const order = heights
    .map((height, index) => ({ height, index }))
    .sort((a, b) => a.height - b.height);
  const shared = [...heights];
  let left = Math.max(0, available);
  order.forEach(({ height, index }, rank) => {
    const share = Math.min(height, left / (order.length - rank));
    shared[index] = share;
    left -= share;
  });
  return shared;
}

/** The column's frames fitted into `height`, reading frames first to give. */
function fitColumn(parts: Part[], height: number): Part[] {
  const total = parts.reduce((sum, part) => sum + part.height, 0);
  if (total <= height) return parts;
  const reading = parts.filter((part) => part.reading);
  const fixed = total - reading.reduce((sum, part) => sum + part.height, 0);
  const readingRoom = height - fixed;
  if (readingRoom >= reading.length * ROUND_FRAME_MIN_H) {
    const shared = shareHeights(
      reading.map((part) => part.height),
      readingRoom,
    );
    let next = 0;
    return parts.map((part) =>
      part.reading ? { ...part, height: shared[next++]! } : part,
    );
  }
  // Reading frames at their minimum; objects, media and tools scale.
  const floored = parts.map((part) =>
    part.reading ? { ...part, height: ROUND_FRAME_MIN_H } : part,
  );
  const objectRoom = height - reading.length * ROUND_FRAME_MIN_H;
  const scale = fixed > 0 ? Math.max(0, objectRoom / fixed) : 1;
  const scaled = floored.map((part) =>
    part.reading
      ? part
      : {
          ...part,
          width: part.width * Math.min(1, scale),
          height: part.height * Math.min(1, scale),
        },
  );
  const scaledTotal = scaled.reduce((sum, part) => sum + part.height, 0);
  if (scale > 0 && scaledTotal <= height) return scaled;
  // Nothing else gives: every frame gets less height, none less width.
  const each = Math.max(1, height) / Math.max(1, total);
  return parts.map((part) => ({ ...part, height: part.height * each }));
}

/** The column's boxes from `left`, `top` down, a margin apart. */
function stackColumn(
  column: Part[],
  left: number,
  top: number,
): FocusWindowBox[] {
  let next = top;
  return column.map((part) => {
    const box = roundBox({
      x: left,
      y: next,
      width: Math.max(1, part.width),
      height: Math.max(1, part.height),
    });
    next = box.y + box.height + FOCUS_WINDOW_MARGIN;
    return box;
  });
}

/**
 * The column beside an anchored main box, centred on it and inside the
 * surface's margins; null when it does not fit to the main box's right.
 */
function columnBesideAnchor(
  surface: FocusWindowBox,
  anchor: FocusWindowBox,
  column: Part[],
  stackHeight: number,
): FocusWindowBox[] | null {
  const gutter = FOCUS_WINDOW_MARGIN;
  if (column.length === 0) return [];
  const left = anchor.x + anchor.width + gutter;
  const width = Math.max(...column.map((part) => part.width));
  if (left + width > surface.x + surface.width - gutter) return null;
  const centred = anchor.y + (anchor.height - stackHeight) / 2;
  const lowest = surface.y + surface.height - gutter - stackHeight;
  const top = Math.max(surface.y + gutter, Math.min(centred, lowest));
  return stackColumn(column, left, Math.round(top));
}

/**
 * The main frame's box first, then one per `side` entry, top to bottom.
 * With `anchor`, the main frame keeps that box while the column fits beside
 * it; otherwise the whole group is centred.
 */
export function calculateRoundGroupBoxes(
  surface: FocusWindowBox,
  mainSize: FocusOriginalSize,
  side: FocusOriginalSize[],
  anchor?: FocusWindowBox | null,
): FocusWindowBox[] {
  const gutter = FOCUS_WINDOW_MARGIN;
  const capWidth = Math.max(
    1,
    (surface.width - gutter * 2) * ROUND_GROUP_MAX_W_RATIO,
  );
  const capHeight = Math.max(
    1,
    (surface.height - gutter * 2) * ROUND_GROUP_MAX_H_RATIO,
  );
  const gap = side.length > 0 ? gutter : 0;
  const parts = side.map(partOf);
  let columnWidth =
    parts.length > 0
      ? Math.min(
          ROUND_COLUMN_MAX_W,
          Math.max(ROUND_FRAME_MIN_W, ...parts.map((part) => part.width)),
        )
      : 0;
  let main = shortenTo(partOf(mainSize), capHeight);
  // The main frame gives way to the column first, down to its minimum width.
  main = narrowTo(
    main,
    Math.max(ROUND_FRAME_MIN_W, capWidth - gap - columnWidth),
  );
  const overflow = main.width + gap + columnWidth - capWidth;
  if (overflow > 0) {
    // A surface too small for both minimums: both narrow in proportion.
    const scale = Math.max(0, (capWidth - gap) / (main.width + columnWidth));
    main = narrowTo(main, Math.max(1, main.width * scale));
    columnWidth = Math.max(1, columnWidth * scale);
  }
  const columnGaps = gutter * Math.max(0, parts.length - 1);
  const column = fitColumn(
    parts.map((part) => narrowTo(part, columnWidth)),
    Math.max(1, capHeight - columnGaps),
  );
  const stackHeight =
    column.reduce((sum, part) => sum + part.height, 0) + columnGaps;
  const anchored =
    anchor && columnBesideAnchor(surface, anchor, column, stackHeight);
  if (anchor && anchored) return [anchor, ...anchored];
  const groupWidth =
    main.width +
    gap +
    (column.length > 0 ? Math.max(...column.map((part) => part.width)) : 0);
  const groupHeight = Math.max(main.height, stackHeight);
  const left = Math.round(surface.x + (surface.width - groupWidth) / 2);
  const top = surface.y + (surface.height - groupHeight) / 2;
  const mainBox = roundBox({
    x: left,
    y: top + (groupHeight - main.height) / 2,
    width: Math.max(1, main.width),
    height: Math.max(1, main.height),
  });
  return [
    mainBox,
    ...stackColumn(
      column,
      mainBox.x + mainBox.width + gap,
      Math.round(top + (groupHeight - stackHeight) / 2),
    ),
  ];
}
